import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trialStatus, setTrialStatus] = useState(null);

  /**
   * Safe setUser that PRESERVES `is_admin` across partial user
   * updates. Root cause of the recurring "admin UI disappears after
   * settings change / redeploy" bug (iter-64…iter-72): endpoints
   * like PUT /auth/settings and PUT /auth/wall-opt-in return the
   * updated user WITHOUT `is_admin` (it's only added by /auth/me).
   * Every setUser(data) from those responses would silently strip
   * is_admin from React state, locking the admin out of Pro-gated
   * routes until the next hard refresh.
   *
   * This wrapper merges `is_admin` from the prior state whenever the
   * incoming user object doesn't explicitly carry it. New user
   * responses can still override by including `is_admin` explicitly
   * (e.g., /auth/me sets true/false based on ADMIN_EMAILS env).
   */
  const setUser = useCallback((next) => {
    setUserRaw((prev) => {
      if (next === null || next === undefined) return next;
      if (Object.prototype.hasOwnProperty.call(next, "is_admin")) return next;
      // No is_admin on incoming → inherit from prior state.
      return { ...next, is_admin: prev?.is_admin };
    });
  }, []);

  const refreshTrial = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/trial-status");
      setTrialStatus(data);
    } catch {
      setTrialStatus(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // /auth/me is the ONLY source of truth for is_admin — always
      // has the field explicitly, so this bypasses the merge shim.
      setUserRaw(data);
      // Runtime guardrail: if the response is missing is_admin (regression),
      // surface it loudly in dev tools so we catch it BEFORE users complain.
      if (!Object.prototype.hasOwnProperty.call(data, "is_admin")) {
        console.warn("[auth-schema-guard] /auth/me response missing is_admin field", data);
      }
      // Fire-and-forget: trial status is a UI-only concern, never gate the login flow.
      refreshTrial();
    } catch (e) {
      setUserRaw(null);
      setTrialStatus(null);
    } finally {
      setLoading(false);
    }
  }, [refreshTrial]);

  useEffect(() => {
    // If returning from OAuth callback, skip /me check — AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore — proceed to clear local state anyway */ }
    setUserRaw(null);
    setTrialStatus(null);
    window.location.href = "/";
  }, []);

  return (
    <AuthContext.Provider value={useMemo(
      () => ({ user, setUser, loading, checkAuth, logout, trialStatus, refreshTrial }),
      [user, setUser, loading, checkAuth, logout, trialStatus, refreshTrial],
    )}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
